import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Button, Dialog, H1, HStack, Icon, Separator, ShimmerText, Text, VStack } from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { HtmlContent } from '~/b4b/components/html-content/HtmlContent';
import { RoundedBorderBox } from '~/b4b/features/plan-benchmark/components/RoundedBorderBox';

import AiOrb from './assets/icon_ai_orb.svg';
import {
  GeneratePlanBenchmarkDocument,
  type GeneratePlanBenchmarkMutation,
} from './graphql/mutation.generatePlanBenchmark.generated';
import { PlanBenchmarkButton } from './PlanBenchmarkButton';

type Props = { planId: string };
type BenchmarkResult = NonNullable<GeneratePlanBenchmarkMutation['generatePlanBenchmark']['result']>;
type RateMetric = BenchmarkResult['participationRate'];

const formatOneDecimal = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
};

const formatRate = (rate: number) => `${formatOneDecimal(rate)}%`;

const formatDiff = (planValue: number, benchmarkValue: number) => {
  const diff = planValue - benchmarkValue;
  if (diff > 0) return `+${formatOneDecimal(diff)}% above the national average`;
  if (diff < 0) return `${formatOneDecimal(Math.abs(diff))}% below the national average`;
  return 'At the national average';
};

const BenchmarkTableHeader = () => (
  <HStack css={{ gap: 'space.200', alignItems: 'center', width: '100%' }}>
    <H1 variant="title100" css={{ width: '200px', flexShrink: 0 }}>
      Benchmark details
    </H1>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      Your plan
    </Text>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      National Avg.*
    </Text>
  </HStack>
);

const BenchmarkDisclosure = ({ content }: { content: DocumentFragment }) => (
  <Box css={{ fontSize: 'fontSize.25', color: 'text.weak', '& a': { color: 'inherit', textDecoration: 'underline' } }}>
    <HtmlContent documentFragment={content} />
  </Box>
);

const AIDisclaimer = () => (
  <Box css={{ color: 'text.weak', fontSize: 'fontSize.25' }}>
    AI summary; accuracy is not guaranteed. See important disclosures and our privacy policy.
  </Box>
);

const BenchmarkSkeleton = () => (
  <VStack css={{ pt: 'space.300', gap: 'space.150', width: 'size.full' }}>
    <ShimmerText textStyle="text50" css={{ width: '65%' }} />
    <ShimmerText textStyle="text25" css={{ width: '100%' }} />
    <ShimmerText textStyle="text25" css={{ width: '90%' }} />
    <ShimmerText textStyle="text25" css={{ width: '55%' }} />
  </VStack>
);

const BenchmarkDetailsRow = ({ label, planValue, benchmarkValue }: RateMetric) => (
  <VStack css={{ gap: 'space.0', width: '100%' }}>
    <HStack css={{ gap: 'space.200', alignItems: 'center', width: '100%' }}>
      <Text variant="text100" css={{ width: '200px', flexShrink: 0 }}>
        {label}
      </Text>
      <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
        {formatRate(planValue)}
      </Text>
      <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
        {formatRate(benchmarkValue)}
      </Text>
    </HStack>
    <Text variant="text50" color={planValue >= benchmarkValue ? 'text.success' : 'text.warning'}>
      {formatDiff(planValue, benchmarkValue)}
    </Text>
  </VStack>
);

const BenchmarkDetails = ({
  benchmarkDisclosure,
  participationRate,
  contributionRate,
}: Omit<BenchmarkResult, 'aiSummary' | '__typename'>) => (
  <VStack css={{ gap: 'space.200', width: '100%' }}>
    <BenchmarkTableHeader />
    <Separator />
    <BenchmarkDetailsRow {...participationRate} />
    <Separator />
    <BenchmarkDetailsRow {...contributionRate} />
    <Separator />
    <BenchmarkDisclosure content={benchmarkDisclosure} />
  </VStack>
);

const AISummary = ({ aiSummary }: { aiSummary: BenchmarkResult['aiSummary'] }) => (
  <RoundedBorderBox borderStyle="gradientRotateOnce" width="size.full">
    <VStack css={{ padding: 'space.300', gap: 'space.150', width: '100%' }}>
      <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
        <img src={AiOrb} alt="" width={24} height={24} />
        <H1 variant="title100">{aiSummary.title}</H1>
      </HStack>

      <Box css={{ fontSize: 'fontSize.100' }}>
        <HtmlContent documentFragment={aiSummary.body} />
      </Box>

      <Box css={{ pt: 'space.100' }}>
        <AIDisclaimer />
      </Box>
    </VStack>
  </RoundedBorderBox>
);

const BenchmarkContent = ({ benchmarkDisclosure, participationRate, contributionRate, aiSummary }: BenchmarkResult) => (
  <VStack css={{ gap: 'space.300', width: '100%', pt: 'space.200' }}>
    <BenchmarkDetails
      benchmarkDisclosure={benchmarkDisclosure}
      participationRate={participationRate}
      contributionRate={contributionRate}
    />
    <AISummary aiSummary={aiSummary} />
  </VStack>
);

const BenchmarkError = ({ onRetry }: { onRetry: () => void }) => (
  <VStack
    css={{
      gap: 'space.200',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'space.400',
      maxWidth: '400px',
      margin: '0 auto',
    }}
  >
    <Icon name="ExclamationCircle" size="medium" color="icon" />
    <Text variant="text100" color="text" css={{ textAlign: 'center' }}>
      We were unable to generate your plan benchmark. Please try again.
    </Text>
    <Button
      variant="solidPrimary"
      onClick={onRetry}
      data-track-event="ElementClicked"
      data-track-name="AIBenchmarkRetry"
      data-track-module="Insights"
      data-track-flow="HomeLanding"
    >
      Try again
    </Button>
  </VStack>
);

export const PlanBenchmarkWidget: React.FC<Props> = ({ planId }) => {
  const [generate, { data, loading, error }] = useGraphQLMutation(GeneratePlanBenchmarkDocument);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (newOpen && !data && !loading) {
      void generate({ variables: { input: { planId } } });
    }
  };

  const result = data?.generatePlanBenchmark.result;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild={true}>
        <PlanBenchmarkButton />
      </Dialog.Trigger>
      <Dialog.Content
        size="large"
        aria-describedby={undefined}
        data-track-event="ElementViewed"
        data-track-name="AIBenchmarkDialog"
        data-track-module="Insights"
        data-track-flow="HomeLanding"
        css={{ p: 'space.400', minWidth: '400px', maxWidth: '640px' }}
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Plan benchmark</H1>
        </Dialog.Title>
        <ErrorBoundary name="plan-benchmark">
          {loading && <BenchmarkSkeleton />}
          {error && !loading && <BenchmarkError onRetry={() => void generate({ variables: { input: { planId } } })} />}
          {result && <BenchmarkContent {...result} />}
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
